import { Element } from "@xmpp/xml"
import type { TachoCardLanguage } from "@/lib/aiWidgetTachoParser"
import {
  isInstant,
  isMinutes,
  isNullable,
  isObject,
  isText,
} from "@/lib/aiWidgetJson"

export const AI_WIDGET_ORDER_XMLNS = "urn:slickshift:ai-widget:order:1"

export type OrderCardStopState = "done" | "here" | "ahead"

export type OrderCardStop = {
  /** gps action code; never shown. */
  action: string | null
  /** The stop's type in the answer's language, written by the agent. */
  label: string | null
  locality: string | null
  countryCode: string | null
  address: string | null
  state: OrderCardStopState
  window: { from: string | null; to: string } | null
  eta: string | null
  etaRestMin: number | null
  /** Minutes `eta` is past `window.to`; null when not late. An agent older than the field sends none: read as null. */
  lateMin: number | null
}

export type OrderCard = {
  version: 1
  language: TachoCardLanguage
  sentence: string
  orderNumber: string | null
  plate: string
  driverName: string
  stops: OrderCardStop[]
  next: { km: number; drivingMin: number } | null
}

const isNonEmptyText = (v: unknown): v is string =>
  typeof v === "string" && v.trim() !== ""
const isState = (v: unknown): v is OrderCardStopState =>
  v === "done" || v === "here" || v === "ahead"
const isKm = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v) && v >= 0

function isWindow(v: unknown): v is NonNullable<OrderCardStop["window"]> {
  return isObject(v) && isNullable(v.from, isInstant) && isInstant(v.to)
}

function isStop(v: unknown): v is Omit<OrderCardStop, "lateMin"> & {
  lateMin?: number | null
} {
  return (
    isObject(v) &&
    isNullable(v.action, isText) &&
    isNullable(v.label, isText) &&
    isNullable(v.locality, isText) &&
    isNullable(v.countryCode, isText) &&
    isNullable(v.address, isText) &&
    isState(v.state) &&
    isNullable(v.window, isWindow) &&
    isNullable(v.eta, isInstant) &&
    isNullable(v.etaRestMin, isMinutes) &&
    (v.lateMin === undefined || isNullable(v.lateMin, isMinutes))
  )
}

function isNext(v: unknown): v is NonNullable<OrderCard["next"]> {
  return isObject(v) && isKm(v.km) && isMinutes(v.drivingMin)
}

export function parseOrderCard(json: string): OrderCard | undefined {
  let data: unknown
  try {
    data = JSON.parse(json)
  } catch {
    return undefined
  }
  if (!isObject(data) || data.version !== 1) return undefined
  if (data.language !== "pl" && data.language !== "en") return undefined
  if (
    isNonEmptyText(data.sentence) &&
    isNullable(data.orderNumber, isNonEmptyText) &&
    isText(data.plate) &&
    isText(data.driverName) &&
    Array.isArray(data.stops) &&
    data.stops.every(isStop) &&
    isNullable(data.next, isNext)
  ) {
    return {
      version: 1,
      language: data.language,
      sentence: data.sentence,
      orderNumber: data.orderNumber,
      plate: data.plate,
      driverName: data.driverName,
      stops: data.stops.map((stop) => ({
        ...stop,
        lateMin: stop.lateMin ?? null,
      })),
      next: data.next,
    }
  }
  return undefined
}

export function parseAiWidgetOrder(stanza: Element): OrderCard | undefined {
  const el = stanza.getChild("ai-widget-order", AI_WIDGET_ORDER_XMLNS)
  return el ? parseOrderCard(el.getText()) : undefined
}
